import { RhUiCheckCircleFillIcon, RhUiErrorFillIcon, RhUiWarningFillIcon } from "@patternfly/react-icons";
// Type-only imports, so pinning the config to PatternFly's own prop types costs nothing at runtime.
import type { IconComponentProps } from "@patternfly/react-core";
import type { SVGIconProps } from "@patternfly/react-icons/dist/esm/createIcon";
import type { ComponentType } from "react";
import { toIsoDate } from "@app/utils/functions";
import { BaseEntity } from "./BaseEntity";
import { Course } from "./Course";
import { SKILL_LEVEL } from "./Enums";
// Type-only as well, and spelt out to keep it that way: Instructor imports this module for its
// skills, so a value import back the other way would be a cycle.
import type { Instructor } from "./Instructor";

export interface Skill extends BaseEntity {
    grantedDate: Date;
    expirationDate: Date;
    courseAttendance: Date;
    deliveryReady: boolean;
    certified: boolean;
    certification: boolean;
    level: SKILL_LEVEL; // EXPERT ADVANCED EXPIRENCED FOUNDATIONAL
    levelLMS: SKILL_LEVEL; // EXPERT ADVANCED EXPIRENCED FOUNDATIONAL
    course: Course;
}

/**
 * Whether an instructor may deliver a given course.
 *
 * Derived rather than stored, and deliberately so: it is a property of the (instructor, course)
 * pair *and* of today's date - the same instructor is certified for one class's course and not
 * for the next one's, and a certification lapses on its expiry date with nothing happening in
 * any system to mark it. Both are reasons a field on the class would be wrong by the time it
 * was read. See getCertification.
 */
export enum CERTIFICATION_STATUS {
    /** Certified, and the certification has not run out - or skilled, on a course with no exam. */
    CERTIFIED = 'CERTIFIED',
    /** Certified once, but the certification has expired - or carries no expiry date to check. */
    EXPIRED = 'EXPIRED',
    /** Nothing on record that says this instructor can deliver the course. */
    NOT_CERTIFIED = 'NOT_CERTIFIED'
}

/**
 * Which rules answered the question, because the two ask different things of a skill row.
 *
 * A skill is one of two kinds - `certification` says which: a Certification (an exam, RH's EXnnn)
 * or a Course. Where an exam exists, holding it is what qualifies an instructor, so `certified`
 * and `expirationDate` decide and a lapsed certification is a real state to report. Where no exam
 * exists there is nothing to hold and nothing to lapse: the course record itself is the evidence -
 * having attended it, at a level that means something - and only "can" and "cannot" apply.
 */
export type CERTIFICATION_BASIS = 'CERTIFICATION' | 'COURSE';

/**
 * What stands in the way where the skill row itself is not the whole answer.
 *
 * Only one so far: delivering an exam takes more than holding the certification, it takes being
 * an examiner (`Instructor.rhcx`). Somebody who holds every certification going still cannot
 * invigilate, so this outranks whatever the skill row says.
 */
export type CERTIFICATION_BLOCKER = 'NOT_AN_EXAMINER';

/** The levels that count as being able to deliver: anything but unskilled, not applicable or unset. */
export const DELIVERABLE_LEVELS: SKILL_LEVEL[] = [
    SKILL_LEVEL.EXPERT,
    SKILL_LEVEL.ADVANCED,
    SKILL_LEVEL.EXPERIENCED,
    SKILL_LEVEL.FOUNDATIONAL
];

/**
 * The one place a certification status's icon and wording are decided, so a status looks the
 * same in the classes table, on the detail pages and in the calendar. `icon` is the component
 * itself rather than an element, so callers can size it themselves; `status` is PatternFly's
 * own Icon status, which colours it from the theme in both light and dark.
 */
export const getCertificationStatusConfig = (status: CERTIFICATION_STATUS) => {
    const map: Record<CERTIFICATION_STATUS, {
        text: string;
        icon: ComponentType<SVGIconProps>;
        status: IconComponentProps['status'];
    }> = {
        [CERTIFICATION_STATUS.CERTIFIED]: {
            text: 'Certified',
            icon: RhUiCheckCircleFillIcon,
            status: 'success'
        },
        [CERTIFICATION_STATUS.EXPIRED]: {
            text: 'Certification expired',
            icon: RhUiWarningFillIcon,
            status: 'warning'
        },
        [CERTIFICATION_STATUS.NOT_CERTIFIED]: {
            text: 'Not certified',
            icon: RhUiErrorFillIcon,
            status: 'danger'
        }
    };
    return map[status];
};

/**
 * A date the service may have sent as a plain "yyyy-MM-dd" string, as that same day.
 *
 * Kept as a string rather than turned into a Date because that is what the comparison needs:
 * an expiry is a calendar day, and `new Date("2026-08-26")` is midnight *UTC*, which lands on
 * the day before in any negative offset. Comparing "yyyy-MM-dd" strings has no zone in it.
 */
const asIsoDay = (value: Date | string | undefined | null): string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') return value.slice(0, 10) || undefined;
    const asDate = new Date(value);
    return isNaN(asDate.getTime()) ? undefined : toIsoDate(asDate);
};

/** The instructor's skill row for a course. The service keeps one row per course, so at most one. */
export const findCourseSkill = (
    instructor: Instructor | undefined | null,
    course: Course | undefined | null
): Skill | undefined =>
    course?.id ? instructor?.skills?.find(skill => skill?.course?.id === course.id) : undefined;

/** What getCertification answers with. */
export interface CertificationCheck {
    status: CERTIFICATION_STATUS;
    /** Which rules decided it, which is also which vocabulary a caller should explain it in. */
    basis: CERTIFICATION_BASIS;
    /** The row the status was read off, absent when the instructor has none for the course. */
    skill?: Skill;
    /** Why the answer is no, where the skill row is not what decided it. */
    blocker?: CERTIFICATION_BLOCKER;
}

/**
 * Whether the course this instructor is being judged for has an exam behind it, and so which
 * rules apply - see CERTIFICATION_BASIS.
 *
 * Three ways it can: the skill row is itself a certification (which is what an *exam* delivery
 * matches, since the class's own course is the exam), the course is an exam, or the course names
 * the exam that certifies it. The skill row's own copy of the course is consulted as well as the
 * class's, so a class loaded outside the store - which is not hydrated from the course store -
 * cannot answer differently from one loaded through it.
 */
const hasExam = (course: Course, skill?: Skill): boolean =>
    skill?.certification === true
    || course.isCertification === true
    || Boolean(course.certification || skill?.course?.certification);

/**
 * The level an instructor is recorded at for a course, as the Instructor Level column shows it.
 *
 * UNSKILLED covers both no row for the course and a row with no level on it: to a reader deciding
 * who can deliver they say the same thing, and the enum has the word for it. `undefined` only
 * where the question cannot be answered at all - no course, or an instructor whose skills never
 * arrived - the same reservation getCertification makes.
 */
export const getSkillLevel = (
    instructor: Instructor | undefined | null,
    course: Course | undefined | null
): SKILL_LEVEL | undefined => {
    if (!course?.id || !instructor?.skills) return undefined;
    return findCourseSkill(instructor, course)?.level || SKILL_LEVEL.UNSKILLED;
};

/**
 * Whether the class is a delivery of the exam itself rather than of a course.
 *
 * The skill row's copy of the course is consulted as well, for the same reason hasExam does it:
 * a class loaded outside the store is not hydrated from the course store.
 */
const isExamDelivery = (course: Course, skill?: Skill): boolean =>
    course.isCertification === true || skill?.course?.isCertification === true;

/**
 * Whether an instructor may deliver a course, which rules said so, and the skill row it was read
 * off - callers show the row's dates and level alongside the status.
 *
 * `undefined` means the question cannot be answered rather than that the answer is bad, and the
 * callers draw nothing at all in that case:
 * - no course to check against, which a pipeline request whose SKU matched nothing has;
 * - an instructor carrying no `skills` array. Every instructor the store holds has one, and
 *   ingestKlasses swaps a class's instructors for the store's, but it falls back to the class's
 *   own copy for an id the store does not hold. Reading that as "not certified" would paint a
 *   red icon on a missing answer.
 *
 * An instructor with an empty skills list is a different thing: that is an answer, and it is
 * NOT_CERTIFIED. So is an instructor with skills but none for this course - nothing was ever
 * recorded about them and this course.
 */
export const getCertification = (
    instructor: Instructor | undefined | null,
    course: Course | undefined | null
): CertificationCheck | undefined => {
    if (!course?.id || !instructor?.skills) return undefined;

    const skill = findCourseSkill(instructor, course);
    const basis: CERTIFICATION_BASIS = hasExam(course, skill) ? 'CERTIFICATION' : 'COURSE';

    // Invigilating an exam takes an examiner, whatever the certifications say - so this is
    // settled before the row is read, and settles it for a row that is not there either.
    if (isExamDelivery(course, skill) && instructor.rhcx !== true) {
        return { status: CERTIFICATION_STATUS.NOT_CERTIFIED, basis, skill, blocker: 'NOT_AN_EXAMINER' };
    }

    if (!skill) return { status: CERTIFICATION_STATUS.NOT_CERTIFIED, basis };

    if (basis === 'COURSE') {
        // No exam to hold, so `certified` and `expirationDate` say nothing here - the service
        // fills them from the exam, and there is none. Attending the course at a level that
        // means something is the whole of the evidence, and there is nothing that can lapse.
        const qualified = Boolean(skill.courseAttendance) && DELIVERABLE_LEVELS.includes(skill.level);
        return {
            status: qualified ? CERTIFICATION_STATUS.CERTIFIED : CERTIFICATION_STATUS.NOT_CERTIFIED,
            basis,
            skill
        };
    }

    if (!skill.certified) return { status: CERTIFICATION_STATUS.NOT_CERTIFIED, basis, skill };

    // No expiry date reads as expired rather than as "never expires": the LMS leaves the field
    // empty on rows it has not reconciled, so an unknown expiry cannot be taken as a current
    // certification. It is the warning icon and not the error one because the instructor *is*
    // certified - it is the date that is missing.
    const expiresOn = asIsoDay(skill.expirationDate);
    if (!expiresOn || expiresOn < toIsoDate(new Date())) return { status: CERTIFICATION_STATUS.EXPIRED, basis, skill };

    return { status: CERTIFICATION_STATUS.CERTIFIED, basis, skill };
};
